import { blankEventTemplate, EventFactory } from "applesauce-core/factories";
import type { KnownEventTemplate } from "applesauce-core/helpers/event";
import type { NostrEvent } from "nostr-tools";
import {
  CONTAINER_REPOSITORY_KIND,
  isValidContainerRepository,
} from "@/casts/ContainerRepository";

type ContainerRepositoryTemplate = KnownEventTemplate<
  typeof CONTAINER_REPOSITORY_KIND
>;

function uniqueValues(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

export class ContainerRepositoryFactory extends EventFactory<
  typeof CONTAINER_REPOSITORY_KIND,
  ContainerRepositoryTemplate
> {
  static linkRepositories(
    event: NostrEvent,
    repoCoordinates: string[],
    relayHint: string | undefined,
    createdAt: number,
  ): ContainerRepositoryFactory {
    if (!isValidContainerRepository(event)) {
      throw new Error("Invalid container repository event");
    }

    const coordinates = uniqueValues(repoCoordinates);
    if (coordinates.length === 0) {
      throw new Error("At least one repository coordinate is required");
    }

    const tags = event.tags
      .filter(([tagName]) => tagName !== "alt")
      .map((tag) => [...tag]);
    const linkedCoordinates = new Set(
      tags
        .filter(([tagName, coordinate]) => tagName === "a" && !!coordinate)
        .map(([, coordinate]) => coordinate),
    );
    for (const coordinate of coordinates) {
      if (linkedCoordinates.has(coordinate)) continue;
      tags.push(relayHint ? ["a", coordinate, relayHint] : ["a", coordinate]);
    }

    const title =
      event.tags.find(([tagName]) => tagName === "title")?.[1]?.trim() ||
      event.tags.find(([tagName]) => tagName === "d")?.[1];

    return new ContainerRepositoryFactory((resolve) =>
      resolve(blankEventTemplate(CONTAINER_REPOSITORY_KIND)),
    )
      .content(event.content)
      .created(createdAt)
      .modifyPublicTags(() => tags)
      .alt(`Container repository: ${title}`);
  }
}
